import {useCallback, useEffect, useMemo, useState} from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  where,
} from 'firebase/firestore';
import {db} from '../firebase';
import {ehrService} from '../services/ehrService';
import {patientService} from '../services/patientService';
import {withFirestoreTimeout} from '../services/firestoreTimeout';
import {
  TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
  TELEHEALTH_SCRIBE_WORKFLOW_TYPE,
  resolveEncounterModality,
} from '../services/encounterUtils';
import {
  cancelScheduledBot,
  createBot,
  updateScheduledBot,
} from '../services/developerService';

export const getDateFromValue = (value: any) => {
  if (!value) return null;
  if (typeof value.toDate === 'function') return value.toDate();
  if (typeof value?.seconds === 'number') return new Date(value.seconds * 1000);
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

export const formatJoinCountdown = (joinDate: any, nowMs: any) => {
  if (!joinDate) return '';
  const diff = joinDate.getTime() - nowMs;
  if (diff <= 0) return 'Joining now...';
  const totalMin = Math.floor(diff / 60000);
  if (totalMin < 1) return 'Joining in <1m';
  if (totalMin < 60) return `Joins in ${totalMin}m`;
  if (totalMin < 24 * 60) {
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    return m === 0 ? `Joins in ${h}h` : `Joins in ${h}h ${m}m`;
  }
  const dateStr = joinDate.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
  });
  const timeStr = joinDate.toLocaleTimeString(undefined, {
    hour: 'numeric',
    minute: '2-digit',
  });
  return `Joins ${dateStr} at ${timeStr}`;
};

export const isUpcomingScheduledBot = (bot: any) => {
  if (!bot) return false;
  if (bot.is_scheduled !== true) return false;
  if ((bot.status || '').toLowerCase() !== 'created') return false;
  return true;
};

export const isFutureScheduledBot = (bot: any, nowMs = Date.now()) => {
  if (!isUpcomingScheduledBot(bot)) return false;
  const joinDate = getDateFromValue(bot.join_at);
  if (!joinDate) return true;
  return joinDate.getTime() > nowMs;
};

export const isScheduledScribeBot = (bot: any) => {
  const mode = (bot?.mode || bot?.bot_mode || '').toLowerCase();
  const workflow = (
    bot?.workflow_type ||
    bot?.workflowType ||
    ''
  ).toLowerCase();
  const modality = resolveEncounterModality(
    mode === 'scribe' ? TELEHEALTH_SCRIBE_WORKFLOW_TYPE : workflow,
  );
  return modality.isScribe;
};

export const getScheduledBotModality = (bot: any) => {
  const mode = (bot?.mode || bot?.bot_mode || '').toLowerCase();
  const workflow = (
    bot?.workflow_type ||
    bot?.workflowType ||
    ''
  ).toLowerCase();
  return resolveEncounterModality(
    mode === 'scribe'
      ? TELEHEALTH_SCRIBE_WORKFLOW_TYPE
      : workflow || TELEHEALTH_ENCOUNTER_WORKFLOW_TYPE,
  );
};

export const isWithin10MinError = (err: any) => {
  if (!err) return false;
  const detail = err.originalError;
  if (!detail) return false;
  if (detail.statusCode !== 400) return false;
  const raw = detail.responseData;
  if (!raw) return false;
  const text =
    typeof raw === 'string'
      ? raw.toLowerCase()
      : JSON.stringify(raw).toLowerCase();
  return (
    text.includes('not enough time') ||
    text.includes('update_bot_failed') ||
    text.includes('reschedule failed')
  );
};

export const extractApiErrorMessage = (err: any, fallback: any) => {
  if (!err) return fallback;
  const raw = err.originalError?.responseData;
  if (raw) {
    try {
      const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      if (parsed?.message) return parsed.message;
      if (parsed?.error) return parsed.error;
    } catch {
      if (typeof raw === 'string' && raw.length < 240) return raw;
    }
  }
  return err.message || fallback;
};

const getRecallBots = async (userId: any, {scheduledOnly = false} = {}) => {
  const botsRef = collection(db, 'RecallBot');
  const constraints = [where('user_id', '==', userId)];
  if (scheduledOnly) constraints.push(where('is_scheduled', '==', true));
  const q = query(botsRef, ...constraints);
  const querySnapshot = await withFirestoreTimeout(getDocs(q), {
    label: 'getRecallBots',
  });

  const bots: any[] = [];
  querySnapshot.forEach(botDoc => {
    bots.push({id: botDoc.id, ...botDoc.data()});
  });

  return bots;
};

const getMeetingHost = (meetingUrl: any) => {
  try {
    return new URL(meetingUrl).hostname.replace(/^www\./, '');
  } catch {
    return meetingUrl || 'Unknown meeting';
  }
};

const buildPatientName = (patient: any) => {
  if (!patient) return '';
  return `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
};

const formatDuration = (conversation: any, bot: any) => {
  const explicitMinutes = conversation?.minutes;
  if (typeof explicitMinutes === 'number' && explicitMinutes > 0) {
    return `${explicitMinutes} min`;
  }

  const start = getDateFromValue(conversation?.start_time || bot?.created_at);
  const end = getDateFromValue(conversation?.end_time);
  if (!start || !end) return null;

  const minutes = Math.max(
    1,
    Math.round((end.getTime() - start.getTime()) / 60000),
  );
  return `${minutes} min`;
};

const conversationHasField = (conversation: any, field: any) =>
  !!conversation && Object.prototype.hasOwnProperty.call(conversation, field);

const pickPatientField = (conversation: any, bot: any, field: any) => {
  if (conversationHasField(conversation, field)) return conversation[field];
  return bot?.[field] ?? null;
};

const pickPatientName = (conversation: any, bot: any) => {
  if (conversationHasField(conversation, 'patient_name'))
    return conversation.patient_name || '';
  if (conversationHasField(conversation, 'patientName'))
    return conversation.patientName || '';
  return bot?.patient_name || '';
};

export const formatScheduledBotLabel = (bot: any) => {
  if (!bot) return '';
  const modality = getScheduledBotModality(bot);
  const language =
    !modality.isScribe && bot.language ? ` - ${bot.language}` : '';
  const patient = bot.patientName ? ` - ${bot.patientName}` : '';
  return `${modality.badge}${language}${patient}`;
};

export const buildScheduledBotRecreatePayload = (bot: any, newJoinAt: any) => {
  const isScribe = isScheduledScribeBot(bot);
  const patientId = bot.localPatientId || bot.patient_id;
  const ehrPatientId = bot.ehrPatientId || bot.ehr_patient_id;
  const patientName = bot.patientName || bot.patient_name;
  return {
    ...(isScribe && {mode: 'scribe'}),
    ...(bot.language && {language: bot.language}),
    meetingURL: bot.meeting_url || bot.meetingURL,
    ...(bot.dialect && {dialect: bot.dialect}),
    ...(patientId && {patientId}),
    ...(ehrPatientId && {ehrPatientId}),
    ...(patientName && {patientName}),
    joinAt: newJoinAt.toISOString(),
  };
};

export function useScheduledBotActions({
  refresh,
  removeScheduledBot,
  toast,
}: Record<string, any> = {}) {
  const [actionBot, setActionBot] = useState<any>(null);
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false);
  const [isActioning, setIsActioning] = useState(false);
  const [scheduleErrorMessage, setScheduleErrorMessage] = useState<any>(null);
  const [scheduleFallback, setScheduleFallback] = useState<any>(null);

  const openReschedule = useCallback((bot: any) => {
    setActionBot(bot);
    setScheduleErrorMessage(null);
    setScheduleFallback(null);
    setIsCancelDialogOpen(false);
    setIsScheduleModalOpen(true);
  }, []);

  const closeReschedule = useCallback(() => {
    if (isActioning) return;
    setIsScheduleModalOpen(false);
    setActionBot(null);
    setScheduleErrorMessage(null);
    setScheduleFallback(null);
  }, [isActioning]);

  const openCancel = useCallback((bot: any) => {
    setActionBot(bot);
    setIsScheduleModalOpen(false);
    setScheduleErrorMessage(null);
    setScheduleFallback(null);
    setIsCancelDialogOpen(true);
  }, []);

  const closeCancel = useCallback(() => {
    if (isActioning) return;
    setIsCancelDialogOpen(false);
    setActionBot(null);
  }, [isActioning]);

  const handleConfirmReschedule = useCallback(
    async (newJoinAt: any) => {
      if (!actionBot) return;
      setIsActioning(true);
      setScheduleErrorMessage(null);
      try {
        await updateScheduledBot(actionBot.id, newJoinAt);
        toast?.({
          title: 'Session rescheduled',
          description: `Now joining at ${newJoinAt.toLocaleString()}.`,
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
        setIsScheduleModalOpen(false);
        setActionBot(null);
        setScheduleFallback(null);
        await refresh?.();
      } catch (err) {
        if (isWithin10MinError(err)) {
          setScheduleFallback('cancel-recreate');
          setScheduleErrorMessage(
            'Too close to the current join time to reschedule. Cancel and reschedule instead?',
          );
        } else {
          const message = extractApiErrorMessage(
            err,
            'Failed to reschedule session.',
          );
          setScheduleErrorMessage(message);
          toast?.({
            title: 'Could not reschedule',
            description: message,
            status: 'error',
            duration: 5000,
            isClosable: true,
          });
        }
      } finally {
        setIsActioning(false);
      }
    },
    [actionBot, refresh, toast],
  );

  const handleCancelAndReschedule = useCallback(
    async (newJoinAt: any) => {
      if (!actionBot) return;
      setIsActioning(true);
      try {
        await cancelScheduledBot(actionBot.id, 'reschedule_within_window');
        await createBot(buildScheduledBotRecreatePayload(actionBot, newJoinAt));

        toast?.({
          title: 'Session rescheduled',
          description: `Cancelled the old session and created a new one for ${newJoinAt.toLocaleString()}.`,
          status: 'success',
          duration: 5000,
          isClosable: true,
        });
        setIsScheduleModalOpen(false);
        setActionBot(null);
        setScheduleFallback(null);
        setScheduleErrorMessage(null);
        await refresh?.();
      } catch (err) {
        const message = extractApiErrorMessage(
          err,
          'Failed to recreate session.',
        );
        setScheduleErrorMessage(message);
        toast?.({
          title: 'Could not reschedule',
          description: message,
          status: 'error',
          duration: 6000,
          isClosable: true,
        });
      } finally {
        setIsActioning(false);
      }
    },
    [actionBot, refresh, toast],
  );

  const handleConfirmCancel = useCallback(
    async (reason: any) => {
      if (!actionBot) return;
      const cancelledId = actionBot.id;
      setIsActioning(true);
      try {
        const result = await cancelScheduledBot(cancelledId, reason);
        removeScheduledBot?.(cancelledId);
        setIsCancelDialogOpen(false);
        setActionBot(null);
        toast?.({
          title:
            result?.mode === 'left_call'
              ? 'Session asked to leave'
              : 'Session cancelled',
          description:
            result?.mode === 'left_call'
              ? 'Opalite had already joined; it has been asked to leave the call.'
              : 'The scheduled session was removed.',
          status: 'success',
          duration: 4000,
          isClosable: true,
        });
      } catch (err) {
        const message = extractApiErrorMessage(
          err,
          'Failed to cancel session.',
        );
        toast?.({
          title: 'Could not cancel',
          description: message,
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      } finally {
        setIsActioning(false);
      }
    },
    [actionBot, removeScheduledBot, toast],
  );

  return {
    actionBot,
    isScheduleModalOpen,
    isCancelDialogOpen,
    isActioning,
    scheduleErrorMessage,
    scheduleFallback,
    openReschedule,
    closeReschedule,
    openCancel,
    closeCancel,
    handleConfirmReschedule,
    handleCancelAndReschedule,
    handleConfirmCancel,
  };
}

// Snapshot cache in the planner-module-cache style: the enriched bot list is
// replayed on remount so re-entering the planner doesn't hold the schedule
// behind the bots -> conversations -> patients enrichment chain. A manual
// refresh (refreshKey bump or refresh()) always re-fetches and overwrites.
const BOTS_SNAPSHOT_CACHE = new Map<string, Record<string, any>>();
const BOTS_SNAPSHOT_TTL_MS = 5 * 60 * 1000;
const BOTS_SNAPSHOT_LIMIT = 5;

export const clearScheduledBotsSnapshotCache = () => {
  BOTS_SNAPSHOT_CACHE.clear();
};

const botsSnapshotKey = (userId: any, companyId: any, scheduledOnly: any) =>
  `${userId || ''}|${companyId || ''}|${scheduledOnly ? 1 : 0}`;

const readBotsSnapshot = (key: string) => {
  const cached = BOTS_SNAPSHOT_CACHE.get(key);
  if (!cached) return null;
  if (Date.now() - (cached.cachedAt || 0) >= BOTS_SNAPSHOT_TTL_MS) {
    BOTS_SNAPSHOT_CACHE.delete(key);
    return null;
  }
  return cached;
};

const writeBotsSnapshot = (key: string, bots: any[]) => {
  if (!key) return;
  if (BOTS_SNAPSHOT_CACHE.has(key)) BOTS_SNAPSHOT_CACHE.delete(key);
  BOTS_SNAPSHOT_CACHE.set(key, {bots, cachedAt: Date.now()});
  while (BOTS_SNAPSHOT_CACHE.size > BOTS_SNAPSHOT_LIMIT) {
    const oldestKey = BOTS_SNAPSHOT_CACHE.keys().next().value;
    if (!oldestKey) break;
    BOTS_SNAPSHOT_CACHE.delete(oldestKey);
  }
};

/**
 * RETIRED FROM THE LIVE SCHEDULE PATH: the planner Daily Schedule (its only
 * page-level consumer) now receives scheduled bots pre-composed by
 * GET /api/v2/views/schedule (see usePlannerSchedule). The hook itself is
 * kept for its tests and as the reference implementation of the direct
 * RecallBot read; the pure helpers above (getDateFromValue,
 * formatJoinCountdown, getScheduledBotModality, error mappers) and the
 * snapshot-cache clear remain in active use (ScheduledSessionDetailPanel,
 * PlannerBrowserColumn). Do not add new callers — compose server-side.
 */
export default function useScheduledBots({
  userId,
  companyId,
  enabled = true,
  refreshKey = 0,
  scheduledOnly = false,
}: Record<string, any> = {}) {
  const [bots, setBots] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const id = setInterval(() => setNowMs(Date.now()), 60_000);
    return () => clearInterval(id);
  }, []);

  const refresh = useCallback(async () => {
    if (!enabled || !userId) {
      setBots([]);
      setIsLoading(false);
      setError(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const fetchedBots = await getRecallBots(userId, {scheduledOnly});
      const sortedBots = fetchedBots.sort((a, b) => {
        const dateA = getDateFromValue(a.created_at)?.getTime() || 0;
        const dateB = getDateFromValue(b.created_at)?.getTime() || 0;
        return dateB - dateA;
      });

      const conversations = await Promise.all(
        sortedBots.map(async bot => {
          if (!bot.telecall_id) return [bot.id, null];
          try {
            const conversationSnap = await withFirestoreTimeout(
              getDoc(doc(db, 'Conversation', bot.telecall_id)),
              {label: 'getRecallBots:Conversation'},
            );
            return [
              bot.id,
              conversationSnap.exists() ? conversationSnap.data() : null,
            ];
          } catch (conversationError) {
            console.warn(
              `Skipping Conversation ${bot.telecall_id} for bot ${bot.id}:`,
              conversationError?.message || conversationError,
            );
            return [bot.id, null];
          }
        }),
      );
      const conversationByBotId = new Map<any, any>(
        conversations.map((e: any) => [e[0], e[1]]),
      );

      const localPatientIds = [
        ...new Set(
          sortedBots
            .map(bot =>
              pickPatientField(
                conversationByBotId.get(bot.id),
                bot,
                'patient_id',
              ),
            )
            .filter(Boolean),
        ),
      ];
      const ehrPatientIds = [
        ...new Set(
          sortedBots
            .map(bot =>
              pickPatientField(
                conversationByBotId.get(bot.id),
                bot,
                'ehr_patient_id',
              ),
            )
            .filter(Boolean),
        ),
      ];
      // Local and EHR patient lookups only depend on the conversations above,
      // not on each other — resolve them concurrently.
      const [localPatients, ehrPatientMap] = await Promise.all([
        Promise.all(
          localPatientIds.map(async patientId => {
            try {
              return [patientId, await patientService.getPatient(patientId)];
            } catch (patientError) {
              console.warn(
                `Skipping local patient ${patientId}:`,
                patientError?.message || patientError,
              );
              return [patientId, null];
            }
          }),
        ),
        companyId && ehrPatientIds.length > 0
          ? ehrService.fetchCachedPatientsByIds(companyId, ehrPatientIds)
          : Promise.resolve({} as Record<string, any>),
      ]);
      const localPatientMap = new Map<any, any>(
        localPatients.map((e: any) => [e[0], e[1]]),
      );

      const enrichedBots = sortedBots.map(bot => {
        const conversation = conversationByBotId.get(bot.id);
        const localPatientId = pickPatientField(
          conversation,
          bot,
          'patient_id',
        );
        const localPatient = localPatientId
          ? localPatientMap.get(localPatientId)
          : null;
        const ehrPatientIdFromConversation = pickPatientField(
          conversation,
          bot,
          'ehr_patient_id',
        );
        const ehrPatientId =
          ehrPatientIdFromConversation ||
          (!conversationHasField(conversation, 'ehr_patient_id')
            ? localPatient?.fhir_patient_id || null
            : null);
        const ehrPatient = ehrPatientId ? ehrPatientMap[ehrPatientId] : null;
        const rawPatientName = pickPatientName(conversation, bot);
        const patientName =
          rawPatientName ||
          (!conversationHasField(conversation, 'patient_name') &&
          !conversationHasField(conversation, 'patientName')
            ? buildPatientName(localPatient) ||
              (ehrPatient ? ehrService.ehrPatientFullName(ehrPatient) : '')
            : '');
        const transcriptCount =
          typeof conversation?.message_count === 'number'
            ? conversation.message_count
            : Array.isArray(conversation?.messages)
              ? conversation.messages.filter(
                  (message: any) =>
                    (message.content_type || message.contentType) !==
                    'dictated_note',
                ).length
              : 0;

        return {
          ...bot,
          conversation,
          localPatientId,
          ehrPatientId,
          patientName: patientName || '',
          patientMrn: localPatient?.mrn || ehrPatient?.mrn || '',
          providerName:
            conversation?.providerName || conversation?.provider_name || '',
          meetingHost: getMeetingHost(bot.meeting_url),
          transcriptCount,
          durationLabel: formatDuration(conversation, bot),
          summary: conversation?.summary || '',
          startedAt: conversation?.start_time || bot.created_at,
          updatedAt: bot.updated_at || conversation?.end_time || bot.created_at,
        };
      });

      setBots(enrichedBots);
      writeBotsSnapshot(
        botsSnapshotKey(userId, companyId, scheduledOnly),
        enrichedBots,
      );
    } catch (err) {
      setError(err.message || 'Failed to load scheduled sessions.');
    } finally {
      setIsLoading(false);
    }
  }, [companyId, enabled, scheduledOnly, userId]);

  useEffect(() => {
    // Plain mounts replay a fresh snapshot instead of re-running the whole
    // enrichment chain; refreshKey bumps (manual refresh) always re-fetch.
    if (enabled && userId && refreshKey === 0) {
      const cached = readBotsSnapshot(
        botsSnapshotKey(userId, companyId, scheduledOnly),
      );
      if (cached) {
        setBots(cached.bots || []);
        setIsLoading(false);
        setError(null);
        return;
      }
    }
    refresh();
  }, [companyId, enabled, refresh, refreshKey, scheduledOnly, userId]);

  const scheduledBots = useMemo(() => {
    return bots
      .filter(bot => isFutureScheduledBot(bot, nowMs))
      .sort((a, b) => {
        const ta = getDateFromValue(a.join_at)?.getTime() ?? 0;
        const tb = getDateFromValue(b.join_at)?.getTime() ?? 0;
        return ta - tb;
      });
  }, [bots, nowMs]);

  const recentBots = useMemo(() => {
    return bots.filter(bot => {
      if (isFutureScheduledBot(bot, nowMs)) return false;
      if ((bot.status || '').toLowerCase() === 'cancelled') return false;
      return true;
    });
  }, [bots, nowMs]);

  const removeScheduledBot = useCallback(
    (botId: any) => {
      setBots(prev => prev.filter(bot => bot.id !== botId));
      // Keep the replay snapshot consistent so a cancelled bot can't
      // resurrect on the next mount within the TTL.
      const key = botsSnapshotKey(userId, companyId, scheduledOnly);
      const cached = BOTS_SNAPSHOT_CACHE.get(key);
      if (cached) {
        BOTS_SNAPSHOT_CACHE.set(key, {
          ...cached,
          bots: (cached.bots || []).filter((bot: any) => bot.id !== botId),
        });
      }
    },
    [companyId, scheduledOnly, userId],
  );

  return {
    bots,
    scheduledBots,
    recentBots,
    isLoading,
    error,
    nowMs,
    refresh,
    removeScheduledBot,
  };
}
